/*
 * INVOICES-LIST.JSX - Invoice & Progress Claim Listing Component
 *
 * SUMMARY:
 * Renders the INVOICES page for a project, including progress claim listing,
 * summary cards, retention handling and invoice/progress-claim actions.
 */
import React, { useState, useEffect } from "react";
import "./invoices.scss";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { PROJECT_MANAGEMENT } from "../../../../constants/route-paths";
import ViewInvoice from "./view-invoice";
import { getGlobalSettingTemplateTypes } from "../../../../actions/global-setting-template.action";
import {
  getInvoiceAndProgressClaimList,
  downloadProjectInvoice,
  downloadProjectProgressBreakup,
  deleteProgressClaim as deleteProgressClaimAPI,
  getProjectCustomer,
  revertToDraftProgressClaim as revertToDraftProgressClaimAction,
} from "../../../../actions/invoice.action";
import { format } from "date-fns";
import { numberFormat, toUSD } from "../../../../util/currency-formatter";
import DownloadDialog from "../../../../components/download-dialog";
import DeleteDialog from "../../../../components/delete-dialog";
import Modal from "../../../../components/modal/modal";
import {
  addProgressClaimRetention,
  editProgressClaimRetention,
  getProjectSummary,
} from "../../../../actions/project-management.action";
import idx from "idx";
import { resolveProjectType } from "../../utils/resolve-project-type";
import Breadcrumb from "react-bootstrap/Breadcrumb";
import Popover from "react-bootstrap/Popover";
import Button from "react-bootstrap/Button";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import "../../scss/task.scss";
import { AmountInput } from "../../../../components/inputs/new-input";
import ClaimSummaryCard from "./claim-summary-card";
import InvoiceClaimsPanel from "../../../../components/invoice-claims-panel";
import { getGlobalSettingReducer } from "../../../../actions/global-setting.action";
import _ from "lodash";
import { parseFloatValue, combineData, getVariationNumber } from "../../../../util/common";
import { stageTotal } from "../../../../util/calculations/calculate-stage";
import { optionsTotal } from "../../../../util/calculations/calculate-option";
import { getOptions } from "../../../../actions/project-management.action";
import SyncInvoicesModal from "../../../../components/sync-invoices-modal";
import { fetchAccountingConnections } from "../../../../slice/accounting.slice";

const InvoiceList = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { projectId } = useParams();
  const wFinancePermission = props.wFinancePermission;
  const user = useSelector(state => state.user.userData);
  const accountId = idx(user, _ => _.selected_account.id);
  const letterhead = useSelector(state => state.globalSetting.letterhead);
  const projectSummary = useSelector(state => state.projectSummary) || {};
  const projectState = useSelector(state => state.project);
  const entitlements = useSelector(state => state.user.entitlements) || {};
  const isAccountingEnabled = Boolean(entitlements.accountingIntegration && entitlements.accountingPush);
  const connections = useSelector((state) => state.accounting?.connections) || [];

  const [showSyncModal, setShowSyncModal] = useState(false);

  useEffect(() => {
    if (accountId) {
      dispatch(fetchAccountingConnections());
    }
  }, [accountId, dispatch]);

  const [state, setState] = useState({
    tableData: [],
    columns: [],
    filter: "",
    invoiceData: null,
    progressClaims: [],
    invoices: [],
    projectId: null,
    invoiceDialogState: false,
    downloadData: {
      open: false,
      id: "",
      template: "",
    },
    deleteDialogState: false,
    deleteId: null,
    deleteType: "",
    revertDialogState: false,
    revertClaimId: null,
    anchorEl: null,
    customerPaymentTerms: "",
    invoiceAndProgressClaim: [],
    progressClaimId: "",
    addRetentionDialog: false,
    retentionField: {
      name: "retentionInput",
      label: "",
      type: "number",
      touched: true,
      error: false,
      valid: true,
      value: "",
    },
    projectQuoteType: "",
    totalRetention: "",
    templateOptions: [],
    templateValue: "",
    invoiceTotalAmount: "",
    editRetention: false,
    retentionOverride: null,
    projectData: null,
    contractAmounts: {
      contractPrice: 0,
      variations: 0,
      revisedContract: 0
    },
  });

  useEffect(() => {
    setState((prevState) => ({ ...prevState, projectId }));
    getData();

    const isCurrentProjectInSummary =
      projectSummary?.project?.projectId === projectId &&
      projectSummary?.financialCard?.contract !== undefined;

    if (isCurrentProjectInSummary) {
      setState((prevState) => ({
        ...prevState,
        projectData: projectSummary.project.res,
        contractAmounts: {
          contractPrice: projectSummary.financialCard.contract,
          variations: projectSummary.financialCard.variations,
          revisedContract: projectSummary.financialCard.revisedContract
        }
      }));
    } else if (!state.projectData && projectId) {
      dispatch(getProjectSummary(projectId)).then(async (projectData) => {
        // Calculate contract amounts
        const contractAmounts = await calculateContractAmounts(projectData);

        setState((prevState) => ({
          ...prevState,
          projectData,
          contractAmounts
        }));
      });
    }
  }, [projectId, dispatch]);

  useEffect(() => {
    if (_.isEmpty(letterhead)) {
      dispatch(getGlobalSettingReducer('LETTERHEAD'));
    }
  }, []);

  // const handleTemplateChange = (e) => {
  //   setState((prevState) => ({
  //     ...prevState,
  //     templateValue: e.target.value,
  //   }));
  // };

  const handleClose = () => {
    setState((prevState) => ({
      ...prevState,
      anchorEl: null,
    }));
  };

  const handleMenu = (tableData) => {
    // setState((prevState) => ({ ...prevState, tableData }));
  };

  const getData = async () => {
    let projectQuoteType = state.projectQuoteType;
    await dispatch(getInvoiceAndProgressClaimList(`?projectId=${projectId}&type=list`)).then((res) => {
      let columns = [
        {
          title: "Invoice Number",
          field: "claimNumber",
          render: (rowData) => (
            <span className="link" onClick={() => {
              if (rowData.progressClaimId) invoiceDialogHandler(rowData);
              else handleGeneratePC(rowData.u_id);
            }}>
              {rowData.invoice.id}
            </span>
          ),
        },
        {
          title: "Invoice Status",
          field: "progressClaimStatus",
          render: (rowData) => rowData.progressClaimStatus ? rowData.progressClaimStatus : "DRAFT",
        },
        {
          title: "Progress Claim Id",
          field: "id",
          render: (rowData) => (rowData.progressClaimId ? rowData.id : ""),
        },
        {
          title: "Claim Amount",
          field: "claimAmount",
          render: (rowData) => toUSD(rowData.claimAmount),
        },
        {
          title: "Total Claimed Amount",
          field: "totalValue",
          render: (rowData) => toUSD(rowData.totalValue),
        },
        {
          title: "Review/Retention Amount",
          field: "retentionAmount",
          render: (rowData) =>
            toUSD(projectQuoteType === "noquote" ? 0 : rowData.retentionAmount || 0),
        },
        {
          title: "Issue Date",
          field: "invoiceDate",
          render: (rowData) => rowData.progressClaimId && (rowData.invoiceDate ? format(new Date(rowData.invoiceDate), "dd/MM/yyyy") : ""),
        },
        {
          title: "Due Date",
          field: "dueDate",
          render: (rowData) => rowData.progressClaimId && (rowData.dueDate ? format(new Date(rowData.dueDate), "dd/MM/yyyy") : ""),
        },
      ];

      const invoiceAndProgressClaim = JSON.parse(JSON.stringify(res));
      let totalRetention = 0;
      let tableData = invoiceAndProgressClaim.filter((item) => {
        projectQuoteType = item.project.quote_type;
        if (item.progressClaimId) totalRetention += item.retentionAmount || 0;
        else totalRetention += item.retentionAmount || 0;
        item.retentionAmount = (item.retentionAmount || 0);

        return item;
      });

      setState((prevState) => ({
        ...prevState,
        tableData,
        columns,
        invoiceAndProgressClaim,
        totalRetention,
        projectQuoteType,
      }));
    });
  };

  const invoiceDialogHandler = async (data, type) => {
    let res = {}, claimsRetainedTotal = 0, claimsRetentionClaimTotal = 0;
    if (!state.invoiceDialogState && type === "PROGRESS_CLAIM") {
      res = await dispatch(getProjectCustomer(projectId)).catch(e => console.error(e));
      Array.isArray(state.invoiceAndProgressClaim) &&
        state.invoiceAndProgressClaim.filter((claim) => {
          const progressClaimId = claim.progressClaimId ? claim.progressClaimId : claim.id;
          if (progressClaimId !== data.id) {
            claimsRetainedTotal += claim.retentionAmount;
            claimsRetentionClaimTotal += claim.retentionClaimAmount;
          }
        });
    }
    setState((prevState) => ({
      ...prevState,
      invoiceData: data,
      filter: data && (data.isInvoiceGenerated === true ? "INVOICES" : type || "PROGRESSCLAIM"),
      invoiceDialogState: !prevState.invoiceDialogState,
      customerPaymentTerms: !prevState.invoiceDialogState ? res?.customer?.paymentTerms : "",
      claimsRetainedTotal,
      claimsRetentionClaimTotal,
    }));
    handleClose();
  };

  // const cancelHandler = () => {
  //   navigate(`/${PROJECT_MANAGEMENT}/view/${projectId}`);
  // };

  const gettemplate = async (type) => {
    let templateOptions = [];
    const templateData = await dispatch(getGlobalSettingTemplateTypes(type, projectId)).catch(e => console.error(e));
    for (let key in templateData) {
      templateOptions.push({
        value: templateData[key].id,
        key: templateData[key].name,
      });
    }
    setState((prevState) => ({ ...prevState, templateOptions }));
  };

  const handleDownloadDialog = (id = "", template = "", stateParam) => {
    let downloadData = { ...state.downloadData };
    if (id) {
      downloadData.open = !downloadData.open;
      downloadData.id = id;
      downloadData.template = template;
      if (downloadData.template) gettemplate(downloadData.template);
      setState((prevState) => ({ ...prevState, downloadData }));
      if (stateParam === "PROGRESS_CLAIM") getData();
    }
  };

  const downloadFile = async (fileFormat = "pdf", tempId) => {
    const { downloadData } = state;
    if (downloadData.template === "PROGRESSCLAIM") {
      if (fileFormat === "pdf" || fileFormat === "ALL") {
        await dispatch(downloadProjectProgressBreakup(downloadData.id, fileFormat, tempId)).catch((e) => console.error(e));
      }
      if (fileFormat === "docx" || fileFormat === "ALL") {
        await dispatch(downloadProjectProgressBreakup(downloadData.id, fileFormat, tempId)).catch((e) => console.error(e));
      }
    } else if (downloadData.template === "INVOICE") {
      if (fileFormat === "pdf" || fileFormat === "ALL") {
        await dispatch(downloadProjectInvoice(downloadData.id, fileFormat, tempId)).catch((e) => console.error(e));
      }
      if (fileFormat === "docx" || fileFormat === "ALL") {
        await dispatch(downloadProjectInvoice(downloadData.id, fileFormat, tempId)).catch((e) => console.error(e));
      }
    }
    handleDownloadDialog();
    handleClose();
  };

  const handleGeneratePC = (progressClaimId) => {
    if (progressClaimId) navigate(`/${PROJECT_MANAGEMENT}/invoice/${projectId}/edit/${progressClaimId}`);
    else navigate(`/${PROJECT_MANAGEMENT}/invoice/${projectId}/create`);

    handleClose();
  };

  const deleteProgressClaim = (id, type) => {
    dispatch(deleteProgressClaimAPI(id, type)).then(() => {
      getData();
      setState((prevState) => ({
        ...prevState,
        deleteDialogState: false,
      }));
    }).catch(e => console.error(e));
    handleClose();
  };

  const deleteDescription = () => {
    const deleteData = state.tableData.find((_) => _.progressClaimId === state.deleteId);
    const invoiceNo = idx(deleteData, (_) => _.id) || "";
    return `Progress Claim - ${invoiceNo}`;
  };

  const revertDescription = () => {
    const revertData = state.tableData.find((_) => _.u_id === state.revertClaimId);
    const claimNo = idx(revertData, (_) => _.id) || "";
    return claimNo ? `Progress Claim ${claimNo}` : "this progress claim";
  };

  const deleteDialogHandler = (id = "", type) => {
    setState((prevState) => ({
      ...prevState,
      deleteDialogState: !prevState.deleteDialogState,
      deleteType: type,
      deleteId: id,
    }));
    handleClose();
  };

  /**
   * Opens the revert to draft confirmation dialog
   * @param {string} progressClaimId - The UUID of the progress claim to revert
   */
  const handleRevertToDraft = (progressClaimId) => {
    if (!progressClaimId) return;

    setState((prevState) => ({
      ...prevState,
      revertDialogState: true,
      revertClaimId: progressClaimId,
    }));
    handleClose();
  };

  /**
   * Closes the revert to draft confirmation dialog
   */
  const revertDialogClose = () => {
    setState((prevState) => ({
      ...prevState,
      revertDialogState: false,
      revertClaimId: null,
    }));
  };

  /**
   * Executes the revert to draft action
   */
  const confirmRevertToDraft = async () => {
    const { revertClaimId } = state;
    if (!revertClaimId) return;

    try {
      await dispatch(revertToDraftProgressClaimAction(revertClaimId));
      getData();
      revertDialogClose();
    } catch (error) {
      console.error("Error reverting claim to draft:", error);
    }
  };

  const handleRetentionDialog = () => {
    const { retentionField } = state;
    setState((prevState) => ({
      ...prevState,
      addRetentionDialog: false,
      retentionField: {
        ...retentionField,
        value: "",
        valid: true,
        error: false,
      },
      progressClaimId: "",
      invoiceTotalAmount: "",
      editRetention: false,
    }));
    handleClose();
  };

  const submitForm = async () => {
    const { retentionField, invoiceAndProgressClaim, progressClaimId, editRetention, retentionOverride } = state;

    const index = invoiceAndProgressClaim.findIndex((c) => c.progressClaimId === progressClaimId);
    if (index !== -1) {
      const invoice = invoiceAndProgressClaim[index];
      if (invoice.claimAmount <= retentionField.value && retentionField.value < 0) {
        setState((prevState) => ({
          ...prevState,
          retentionField: {
            ...prevState.retentionField,
            touched: true,
            valid: false,
            error: "Should be Less than or Equal to Claim Amount!",
          },
        }));
      } else {
        if (editRetention) {
          await dispatch(editProgressClaimRetention({
            ...(retentionOverride ? {
              isManualOverride: retentionOverride.isManualOverride,
              lessRetention: retentionOverride.less,
              addRetention: retentionOverride.add
            } : {}),
          }, progressClaimId)).catch(e => console.error(e));
        } else {
          await dispatch(addProgressClaimRetention({
            progressClaimId: progressClaimId,
            type: "RETENTION",
            ...(retentionOverride ? {
              isManualOverride: retentionOverride.isManualOverride,
              lessRetention: retentionOverride.less,
              addRetention: retentionOverride.add
            } : {}),
          })).catch(e => console.error(e));
        }

        getData();
        handleRetentionDialog();
      }
    }
  };

  const handleRetentionAmount = (e) => {
    let { retentionField, invoiceAndProgressClaim, progressClaimId } = state;
    const amount = parseFloat(e.target.value || 0);
    if (amount < 0) return;

    const index = invoiceAndProgressClaim.findIndex((c) => c.progressClaimId === progressClaimId);
    if (index !== -1) {
      const invoice = invoiceAndProgressClaim[index];
      if (invoice.claimAmount <= amount) {
        retentionField.touched = true;
        retentionField.valid = false;
        retentionField.error = "Should be Less than or Equal to Claim Amount!";
        retentionField.value = amount;
      } else {
        retentionField.touched = true;
        retentionField.valid = true;
        retentionField.error = "";
        retentionField.value = amount;
      }
      setState((prevState) => ({ ...prevState, retentionField }));
    }
  };

  const openAddRetentionDialog = (progressClaim) => {
    const { retentionAmount, u_id } = progressClaim;
    let { retentionField } = state;
    retentionField.value = retentionAmount;
    let editRetention = retentionField.value >= 0 ? true : false;
    let invoiceTotalAmount = progressClaim.totalValue;
    setState((prevState) => ({
      ...prevState,
      addRetentionDialog: true,
      progressClaimId: u_id,
      retentionField,
      invoiceTotalAmount,
      editRetention,
      retentionOverride: null,
    }));
    handleClose();
  };

  const ViewProjectList = () => {
    let type = resolveProjectType(idx(state, (_) => _.projectData.projectType), projectState);
    navigate(`/${PROJECT_MANAGEMENT}?project-type=${type}`);
  };

  const ViewProject = () => {
    navigate(`/${PROJECT_MANAGEMENT}/view/${projectId}`);
  };

  // Helper function to get appropriate badge color based on status
  const getStatusBadgeColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'DRAFT':
      case null:
      case undefined:
        return 'bg-secondary';
      case 'APPROVED':
        return 'bg-warning';
      case 'FINALISED':
      case 'PAID':
        return 'bg-success';
      case 'AWAITING PAYMENT':
      case 'AWAITING_PAYMENT':
        return 'bg-info text-dark';
      case 'REJECTED':
      case 'OVERDUE':
        return 'bg-danger';
      default:
        return 'bg-secondary';
    }
  };

  // Calculate contract amounts using the same logic as get-project-summary.js
  const calculateContractAmounts = async (projectData) => {
    if (!projectData || !projectData.stages) return { contractPrice: 0, variations: 0, revisedContract: 0 };

    // Get options/variations data
    const optionList = await dispatch(getOptions(projectId, { type: 'invoice' }));
    const optionData = getVariationNumber(optionList);
    const optionCalculation = optionsTotal({ options: optionData, projectType: projectData.type });
    const variationData = optionData.filter(_ => !['REJECTED', 'CANCELLED'].includes(_.proposal_status));

    // Process stages data
    let stages = idx(projectData, _ => _.stages) || [];
    const sectionData = combineData({ stages, variation: variationData }) || [];

    // Calculate stage totals
    const stageData = stageTotal(sectionData);

    // Calculate revised project total
    const revisedProjectTotal = {
      totalSaleAmount: stageData.projectTotal.totalSaleAmount + optionCalculation.projectTotal.totalSaleAmount
    };

    return {
      contractPrice: stageData.projectTotal.totalSaleAmount,
      variations: optionCalculation.projectTotal.totalSaleAmount,
      revisedContract: revisedProjectTotal.totalSaleAmount
    };
  };

  const {
    filter,
    tableData,
    invoiceDialogState,
    invoiceData,
    invoices,
    downloadData,
    deleteDialogState,
    deleteId,
    deleteType,
    revertDialogState,
    customerPaymentTerms,
    addRetentionDialog,
    retentionField,
    totalRetention,
    invoiceTotalAmount,
    claimsRetainedTotal,
    templateOptions,
    projectQuoteType,
    claimsRetentionClaimTotal,
    invoiceAndProgressClaim,
    contractAmounts,
    projectData
  } = state;

  // ─── Claims Table: column definitions ───────────────────────────────────────
  const claimsColumns = [
    {
      title: 'Claim Number',
      field: 'claimNumber',
      render: (rowData) => rowData.isSummary ? (
        <strong>Total Claims Approved</strong>
      ) : (
        <span
          className="link claim-link"
          data-cy="claim-number-link"
          onClick={() => invoiceDialogHandler(rowData)}
        >
          {rowData.claimNumber}
        </span>
      )
    },
    {
      title: 'Invoice Number',
      field: 'invoice.id',
      render: (rowData) => rowData.isSummary ? '' : (rowData.invoice?.id || '')
    },
    {
      title: 'Date',
      field: 'invoiceDate',
      render: (rowData) => rowData.isSummary ? '' : (rowData.invoiceDate ? format(new Date(rowData.invoiceDate), 'dd/MM/yyyy') : '')
    },
    {
      title: 'Amount Claimed',
      field: 'claimAmount',
      render: (rowData) => (
        <strong className={rowData.isSummary ? 'summary-text' : ''}>
          {rowData.isSummary ? toUSD(rowData.totalAmountClaimed) : toUSD(rowData.claimAmount)}
        </strong>
      )
    },
    {
      title: 'Retention',
      field: 'retentionAmount',
      render: (rowData) => {
        const retentionAmount = rowData.retentionAmount || 0;
        if (rowData.isSummary) return <span>{toUSD(rowData.totalRetention)}</span>;
        return retentionAmount > 0
          ? <strong className="text-danger">-{toUSD(retentionAmount)}</strong>
          : <strong className="text-success">{toUSD(retentionAmount)}</strong>;
      }
    },
    {
      title: 'Retention Released',
      field: 'retentionClaimAmount',
      render: (rowData) => {
        const retentionClaimAmount = rowData.retentionClaimAmount || 0;
        if (rowData.isSummary) return <span>{toUSD(rowData.totalRetentionClaimed)}</span>;
        return <span>{toUSD(retentionClaimAmount)}</span>;
      }
    },
    {
      title: 'Sub Total',
      field: 'subTotal',
      render: (rowData) => {
        const subTotal = rowData.isSummary
          ? rowData.totalAmountClaimed - rowData.totalRetention + (rowData.totalRetentionClaimed || 0)
          : rowData.claimAmount - (rowData.retentionAmount || 0) + (rowData.retentionClaimAmount || 0);
        return <strong className={rowData.isSummary ? 'summary-text' : ''}>{toUSD(subTotal)}</strong>;
      }
    },
    {
      title: 'GST',
      field: 'gst',
      render: (rowData) => {
        const approved = rowData.isSummary
          ? rowData.totalAmountClaimed - rowData.totalRetention + (rowData.totalRetentionClaimed || 0)
          : rowData.claimAmount - (rowData.retentionAmount || 0) + (rowData.retentionClaimAmount || 0);
        const gstRate = parseFloatValue(numberFormat(letterhead?.finance_gst || 0, true) / 100);
        return <strong className={rowData.isSummary ? 'summary-text' : ''}>{toUSD(approved * gstRate)}</strong>;
      }
    },
    {
      title: 'Total Inc GST',
      field: 'totalClaimedIncGst',
      render: (rowData) => {
        if (!rowData.isSummary && (rowData.progressClaimStatus === 'Draft' || rowData.progressClaimStatus == null)) return '';
        if (rowData.isSummary) return <strong className="summary-text">{toUSD(rowData.totalClaimedIncGst || 0)}</strong>;
        const approved = rowData.claimAmount - (rowData.retentionAmount || 0) + (rowData.retentionClaimAmount || 0);
        const gstRate = parseFloatValue(numberFormat(letterhead?.finance_gst || 0, true) / 100);
        return <strong>{toUSD(approved + approved * gstRate)}</strong>;
      }
    },
    {
      title: 'Status',
      field: 'progressClaimStatus',
      render: (rowData) => rowData.isSummary ? '' : (
        <span className={`badge ${getStatusBadgeColor(rowData.progressClaimStatus)}`}>
          {rowData.progressClaimStatus || 'DRAFT'}
        </span>
      )
    },
    {
      title: 'Action',
      field: 'action',
      render: (rowData) => rowData.isSummary ? '' : (
        <div className="action-dropdown">
          <OverlayTrigger trigger="click" rootClose placement="bottom" overlay={(
            <Popover id="popover-basic">
              <Popover.Body>
                <ul className="action-block-list">
                  {rowData.progressClaimStatus === 'Finalised' && (
                    <>
                      <li onClick={() => invoiceDialogHandler(rowData)}>View</li>
                      <li onClick={() => handleDownloadDialog(rowData.u_id, 'INVOICE')}>Download</li>
                    </>
                  )}
                  {rowData.progressClaimStatus !== 'Finalised' && rowData.progressClaimStatus === 'Approved' && (
                    <>
                      <li data-cy="revert-to-draft-action" onClick={() => handleRevertToDraft(rowData.u_id)}>Revert to Draft</li>
                      <li data-cy="finalise-action" onClick={() => openAddRetentionDialog(rowData)}>Finalise</li>
                      <li onClick={() => invoiceDialogHandler(rowData)}>View</li>
                      <li onClick={() => handleDownloadDialog(rowData.u_id, 'INVOICE')}>Download</li>
                      <li onClick={() => deleteDialogHandler(rowData.u_id, 'INVOICED')}>Delete</li>
                    </>
                  )}
                  {(rowData.progressClaimStatus === 'Draft' || rowData.progressClaimStatus == null) && (
                    <>
                      <li onClick={() => invoiceDialogHandler(rowData, 'PROGRESS_CLAIM')}>Mark as Approved</li>
                      <li onClick={() => handleGeneratePC(rowData.u_id)}>Edit</li>
                      <li onClick={() => invoiceDialogHandler(rowData)}>View</li>
                      <li onClick={() => handleDownloadDialog(rowData.u_id, 'PROGRESSCLAIM')}>Download</li>
                      <li onClick={() => deleteDialogHandler(rowData.u_id, 'DRAFT')}>Delete</li>
                    </>
                  )}
                </ul>
              </Popover.Body>
            </Popover>
          )}>
            <Button variant="default" data-cy="action-menu-button" onClick={() => handleMenu(rowData)}>
              <MoreHorizIcon />
            </Button>
          </OverlayTrigger>
        </div>
      )
    }
  ];

  // ─── Claims Table: pre-processed data with summary row ───────────────────────
  const claimsTableData = (() => {
    const rows = tableData.map(item => ({ ...item, isSummary: false }));
    const totals = rows.reduce((acc, item) => {
      if (item.progressClaimStatus === 'Draft' || item.progressClaimStatus == null) return acc;
      const claimAmount = item.claimAmount || 0;
      const lessRetention = item.retentionAmount || 0;
      const addRetention = item.retentionClaimAmount || 0;
      const approved = claimAmount - lessRetention + addRetention;
      const gstRate = parseFloatValue(numberFormat(letterhead?.finance_gst || 0, true) / 100);
      const totalIncGst = approved + approved * gstRate;
      acc.totalAmountClaimed += claimAmount;
      acc.totalRetention += lessRetention;
      acc.totalRetentionClaimed += addRetention;
      acc.totalClaimedIncGstCents += Math.round((totalIncGst || 0) * 100);
      return acc;
    }, { totalAmountClaimed: 0, totalRetention: 0, totalRetentionClaimed: 0, totalClaimedIncGstCents: 0 });
    if (rows.length > 0) {
      rows.push({
        id: 'summary',
        isSummary: true,
        totalAmountClaimed: totals.totalAmountClaimed,
        totalRetention: totals.totalRetention,
        totalRetentionClaimed: totals.totalRetentionClaimed,
        totalClaimedIncGst: totals.totalClaimedIncGstCents / 100,
        className: 'summary-row'
      });
    }
    return rows;
  })();

  return (
    <>
      <Modal
        open={addRetentionDialog}
        onClose={handleRetentionDialog}
        onClick={submitForm}
        modalHeading={"Finalise Invoice"}
        modalSaveTxt={"Finalise"}
        modalCloseTxt="Cancel"
        showActionBtn={true}
        fullWidth={true}
        maxWidth={"sm"}
        cxlbtnClass="cancelBtn"
        primary
      >
        <div className={`row mb-3 ${projectQuoteType === "noquote" ? "justify-content-center text-center" : ""}`}>
          <div className="col-lg-4 mt-3">
            <div className="view-block">
              <label>Invoice Total</label>
              <p className="mt-2">{toUSD(invoiceTotalAmount)}</p>
            </div>
          </div>
          <div className="col-lg-4 mt-3">
            <div className="view-block">
              <label>Total Retained To Date</label>
              <p className="mt-2" data-cy="total-retained-to-date">${(totalRetention)}</p>
            </div>
          </div>
          {projectQuoteType !== "noquote" && (
            <div className="col-lg-4 mt-3">
              <AmountInput
                {...retentionField}
                label={"Retention Amount"}
                onChange={(e) => handleRetentionAmount(e)}
                data-cy="finalise-retention-input"
              />
            </div>
          )}
        </div>
      </Modal>
      <div className="invoiceContainer">
        <DeleteDialog
          open={deleteDialogState}
          id={deleteId}
          deleteHandler={deleteProgressClaim}
          deleteType={deleteType}
          dialogClose={deleteDialogHandler}
          moduleName="Progress Claim"
          description={deleteDescription()}
        />
        <Modal
          open={revertDialogState}
          onClose={revertDialogClose}
          fullWidth={true}
          maxWidth="sm"
          modalHeading="Revert to Draft"
          modalSaveTxt="Yes"
          modalCloseTxt="Cancel"
          onClick={confirmRevertToDraft}
          showActionBtn
          savebtnClass="createBtn"
          cxlbtnClass="cancelBtn"
          primary
          data-cy="revert-to-draft-dialog"
        >
          <p>
            Are you sure you want to revert <strong>{revertDescription()}</strong> back to draft status?
          </p>
        </Modal>
        <DownloadDialog
          {...downloadData}
          downloadFile={downloadFile}
          templateOptions={templateOptions}
          handleDownloadDialog={handleDownloadDialog}
          downloadModuleName="Invoice"
        />
        <SyncInvoicesModal
          show={showSyncModal}
          onHide={() => setShowSyncModal(false)}
          entityType="invoice"
          projectId={projectId}
          projectName={projectData?.name}
          onSyncSuccess={getData}
        />
        <div className="purchase-order-block">
          <div className="row">
            <div className="w-auto">
              <h2 className="content-heading text-uppercase">INVOICES</h2>
              <div className="col-12 breadcrumb-block p-0">
                <Breadcrumb>
                  <Breadcrumb.Item onClick={ViewProjectList}>PROJECTS</Breadcrumb.Item>
                  <Breadcrumb.Item onClick={ViewProject}>{projectData?.name}</Breadcrumb.Item>
                  <Breadcrumb.Item active>INVOICES</Breadcrumb.Item>
                </Breadcrumb>
              </div>
            </div>
            <div className="ms-auto mt-auto mb-2 d-flex w-auto">
              {wFinancePermission && (
                <>
                  {isAccountingEnabled && connections.length > 0 && (
                    <button onClick={() => setShowSyncModal(true)} className="secondarybtn secondarybtn-outline ms-2 btn-ch mt-auto btn btn-primary me-2">
                      SYNC INVOICES
                    </button>
                  )}
                  <button onClick={() => handleGeneratePC()} className="secondarybtn secondarybtn-outline ms-2 btn-ch mt-auto btn btn-primary">
                    GENERATE INVOICE
                  </button>
                </>
              )}
            </div>
          </div>
          <hr className="mt-2"></hr>

          {/* Summary Cards + Claims Table */}
          <InvoiceClaimsPanel
            claimed={toUSD(tableData.filter(item => item.progressClaimStatus !== 'Draft').reduce((sum, item) => sum + (item.claimAmount || 0), 0))}
            retention={toUSD(tableData.filter(item => item.progressClaimStatus !== 'Draft').reduce((sum, item) => {
              const lessRetention = item.retentionAmount || 0;
              const addRetention = item.retentionClaimAmount || 0;
              return sum + lessRetention - addRetention;
            }, 0))}
            approved={toUSD(tableData.filter(item => item.progressClaimStatus !== 'Draft').reduce((sum, item) => {
              const netRetention = (item.retentionAmount || 0) - (item.retentionClaimAmount || 0);
              return sum + (item.claimAmount || 0) - netRetention;
            }, 0))}
            columns={claimsColumns}
            data={claimsTableData}
            exportButton={true}
            fileName="Claims List"
            enableSearch={true}
            toolbar={true}
            dataCy="claims-table-custom"
          />

          {/* Project and Retention Summary */}
          <div className="row mt-4">
            <div className="col-md-6">
              <ClaimSummaryCard
                title="PROJECT SUMMARY"
                summaryItems={[
                  { label: "Total Original Contract Amount", value: contractAmounts.contractPrice },
                  { label: "Variations", value: contractAmounts.variations },
                  { label: "Total revised contract amount", value: contractAmounts.revisedContract },
                  {
                    label: "Approved",
                    value: tableData.filter(item => item.progressClaimStatus !== 'Draft').reduce((sum, item) => {
                      const netRetention = (item.retentionAmount || 0) - (item.retentionClaimAmount || 0);
                      return sum + (item.claimAmount || 0) - netRetention;
                    }, 0)
                    // value: tableData.reduce((sum, item) => sum + ((item.claimAmount || 0) - (item.retentionAmount || 0)), 0) 
                  },
                  {
                    label: "Contract Balance",
                    // value: contractAmounts.revisedContract - tableData.reduce((sum, item) => sum + ((item.claimAmount || 0) - (item.retentionAmount || 0)), 0) 
                    value: contractAmounts.revisedContract - tableData.filter(item => item.progressClaimStatus !== 'Draft').reduce((sum, item) => {
                      const netRetention = (item.retentionAmount || 0) - (item.retentionClaimAmount || 0);
                      return sum + (item.claimAmount || 0) - netRetention;
                    }, 0)
                  },
                ]}
                data-cy="project-summary-card"
              />
            </div>
            <div className="col-md-6">
              <ClaimSummaryCard
                title="RETENTION SUMMARY"
                summaryItems={[
                  { label: "Max retention", value: contractAmounts.revisedContract * (projectData?.maxRetentionPercentage / 100) }, // max retention percentage of total contract
                  {
                    label: "Currently withheld",
                    // value: tableData.reduce((sum, item) => sum + (item.retentionAmount || 0), 0) 
                    value: tableData.filter(item => item.progressClaimStatus !== 'Draft').reduce((sum, item) => {
                      const netRetention = (item.retentionAmount || 0) - (item.retentionClaimAmount || 0);
                      return sum + netRetention;
                    }, 0)

                  },
                  {
                    label: "Remaining to be withheld",
                    value: Math.max(0, (contractAmounts.revisedContract * (projectData?.maxRetentionPercentage / 100)) - tableData.filter(item => item.progressClaimStatus !== 'Draft').reduce((sum, item) => {
                      const netRetention = (item.retentionAmount || 0) - (item.retentionClaimAmount || 0);
                      return sum + netRetention;
                    }, 0))
                  },
                ]}
                data-cy="retention-summary-card"
              />
            </div>
          </div>

          {invoiceDialogState && (
            <ViewInvoice
              open={invoiceDialogState}
              filter={filter}
              projectQuoteType={projectQuoteType}
              invoiceData={invoiceData}
              dialogClose={invoiceDialogHandler}
              invoicesCount={invoices.length}
              projectId={projectId}
              handleDownloadDialog={handleDownloadDialog}
              handleGeneratePC={handleGeneratePC}
              customerPaymentTerms={customerPaymentTerms}
              claimsRetainedTotal={claimsRetainedTotal}
              claimsRetentionClaimTotal={claimsRetentionClaimTotal}
              invoiceAndProgressClaim={invoiceAndProgressClaim}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default InvoiceList;